import { Grid } from "@mui/material";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import parse from "html-react-parser";
import Comments from "pages/comment";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import api from "services/api";
import { BlogAvatorBox, BlogLabelButton } from "./blogcard";
import BlogOptions from "./blogoptions";

var headertag = "h2";
var subheadertag = "h3";

const scanBlogContent = (content) => {
  headertag = "";
  subheadertag = "";
  for (let i = 1; i <= 6; i++) {
    if (content.includes("</h" + i.toString() + ">")) {
      headertag = "h" + i.toString();
      for (let j = i + 1; j <= 6; j++) {
        if (content.includes("</h" + j.toString() + ">")) {
          subheadertag = "h" + j.toString();
        }
      }
      if (subheadertag == "") subheadertag = "h" + (i + 1).toString();
      break;
    }
  }
  //console.log('headertag', headertag, 'subheadertag', subheadertag, 'content', content)
  if (headertag == "") return "";
  const lvl1Array = content.split(`<${headertag}`);
  if (lvl1Array.length < 2) return content;
  content = "";
  for (var i = 0; i < lvl1Array.length; i++) {
    if (i > 0) {
      content += `<${headertag}`;
      if (!lvl1Array[i].substring(0, lvl1Array[i].indexOf(">")).includes("id="))
        content += " id='HEADER_" + i.toString() + "'";
    }
    content += lvl1Array[i];
  }
  const lvl2Array = content.split(`<${subheadertag}`);
  if (lvl2Array.length < 2) return content;
  content = "";
  for (var i = 0; i < lvl2Array.length; i++) {
    if (i > 0) {
      content += `<${subheadertag}`;
      if (!lvl2Array[i].substring(0, lvl2Array[i].indexOf(">")).includes("id="))
        content += " id='SUBHEADER_" + i.toString() + "'";
    }
    content += lvl2Array[i];
  }
  return content;
};

/**
 * This renders an item in the table of contents list.
 * scrollIntoView is used to ensure that when a user clicks on an item, it will smoothly scroll.
 */
const Headings = ({ headings, activeId }) => (
  <ul style={{ overflowY: "auto", maxHeight: "90vh" }}>
    <SuiBox p={2}>
      <SuiTypography variant="h5">Contents</SuiTypography>
      {headings.map((heading) => (
        <SuiBox mt={0.7} key={heading.id} className={heading.id === activeId ? "active" : ""}>
          <a href={`#${heading.id}`}>
            <SuiBox sx={{ borderLeft: heading.id === activeId ? "3px solid #344767" : "" }}>
              <SuiTypography
                variant="h6"
                className={heading.id === activeId ? "text-green-700 font-black" : "text-[#344767]"}
                ml={heading.id === activeId ? "3px" : "6px"}
              >
                {heading.title}
              </SuiTypography>
            </SuiBox>
          </a>
          {heading.items.length > 0 && (
            <ul>
              {heading.items.map((child) => (
                <SuiBox key={child.id} className={child.id === activeId ? "active" : ""}>
                  <a href={`#${child.id}`}>
                    <SuiBox
                      mt={0.3}
                      sx={{ borderLeft: child.id === activeId ? "3px solid #344767" : "" }}
                    >
                      <SuiTypography
                        variant="body1"
                        ml={child.id === activeId ? "23px" : "26px"}
                        className={
                          child.id === activeId ? "text-green-700 font-black" : "text-[#344767]"
                        }
                      >
                        {child.title}
                      </SuiTypography>
                    </SuiBox>
                  </a>
                </SuiBox>
              ))}
            </ul>
          )}
        </SuiBox>
      ))}
    </SuiBox>
  </ul>
);

/**
 * Dynamically generates the table of contents list, using any H2s and H3s it can find in the main text
 */
const useHeadingsData = () => {
  const [nestedHeadings, setNestedHeadings] = React.useState([]);

  React.useEffect(() => {
    const headingElements = Array.from(
      document.querySelectorAll("main " + headertag + ", main " + subheadertag)
    );

    // Created a list of headings, with H3s nested
    const newNestedHeadings = getNestedHeadings(headingElements);
    setNestedHeadings(newNestedHeadings);
  }, []);

  return { nestedHeadings };
};

const getNestedHeadings = (headingElements) => {
  const nestedHeadings = [];

  headingElements.forEach((heading, index) => {
    const { innerText: title, id } = heading;
    if (heading.nodeName.toLowerCase() === headertag) {
      nestedHeadings.push({ id, title, items: [] });
    } else if (heading.nodeName.toLowerCase() === subheadertag && nestedHeadings.length > 0) {
      nestedHeadings[nestedHeadings.length - 1].items.push({
        id,
        title,
      });
    }
  });

  return nestedHeadings;
};

const useIntersectionObserver = (setActiveId) => {
  const headingElementsRef = React.useRef({});
  React.useEffect(() => {
    const callback = (headings) => {
      headingElementsRef.current = headings.reduce((map, headingElement) => {
        map[headingElement.target.id] = headingElement;
        return map;
      }, headingElementsRef.current);

      // Get all headings that are currently visible on the page
      const visibleHeadings = [];
      Object.keys(headingElementsRef.current).forEach((key) => {
        const headingElement = headingElementsRef.current[key];
        if (headingElement.isIntersecting) visibleHeadings.push(headingElement);
      });

      const getIndexFromId = (id) => headingElements.findIndex((heading) => heading.id === id);

      // If there is only one visible heading, this is our "active" heading
      if (visibleHeadings.length === 1) {
        setActiveId(visibleHeadings[0].target.id);
        // If there is more than one visible heading,
        // choose the one that is closest to the top of the page
      } else if (visibleHeadings.length > 1) {
        const sortedVisibleHeadings = visibleHeadings.sort(
          (a, b) => getIndexFromId(a.target.id) > getIndexFromId(b.target.id)
        );
        setActiveId(sortedVisibleHeadings[0].target.id);
      }
    };

    const observer = new IntersectionObserver(callback, {
      root: document.querySelector("iframe"),
      rootMargin: "0px",
    });

    const headingElements = Array.from(document.querySelectorAll(headertag + ", " + subheadertag));

    headingElements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, [setActiveId]);
};

/**
 * Renders the table of contents.
 */
const TableOfContents = () => {
  const [activeId, setActiveId] = React.useState();
  const { nestedHeadings } = useHeadingsData();
  useIntersectionObserver(setActiveId);

  return (
    <SuiBox
      className="bg-[#ECE8E1]"
      sx={{
        borderRadius: ({ borders: { borderRadius } }) => borderRadius.lg,
        position: "sticky",
        top: "1%",
      }}
    >
      <SuiBox>
        <Headings headings={nestedHeadings} activeId={activeId} />
      </SuiBox>
    </SuiBox>
  );
};

const BlogViewer = ({ viewblogid }) => {
  const [blog, setBlog] = useState(null);
  const [validTableOfContent, setValidTableOfContent] = useState(false);
  const reload = () => {
    if (viewblogid) {
      try {
        api.blog.get(viewblogid).then(({ data }) => {
          var content = scanBlogContent(data.blog.content);
          if (content != "") {
            data.blog.content = content;
            setValidTableOfContent(true);
          }
          setBlog(data.blog);
          //console.log('blog', data.blog)
          return data;
        });
      } catch (err) {
        toast.error(`getuserblogs failed!`);
        console.log(err);
      }
    }
  };

  useEffect(() => {
    reload();
  }, [viewblogid]);

  return (
    blog && (
      <SuiBox className="h-full p-2">
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={12}>
            <Grid container spacing={5} direction="row" alignItems="stretch">
              <Grid item xs={12} lg={4}>
                <SuiBox className="ml-2 mr-2 justify-between" display="flex" alignItems="start">
                  <SuiBox>
                    {blog.labels.map((label) => {
                      return <BlogLabelButton label={label} />;
                    })}
                  </SuiBox>
                  <SuiBox mt={1}>
                    <BlogOptions blog={blog} />
                  </SuiBox>
                </SuiBox>
                <SuiTypography className="ml-3 mr-3 mt-2" variant="h5" fontWeight="medium">
                  {blog.title}
                </SuiTypography>
                <SuiTypography
                  className="ml-3 mr-3 mt-2"
                  variant="h6"
                  fontWeight="regular"
                  color="text"
                >
                  {blog.summary}
                </SuiTypography>
                <BlogAvatorBox blog={blog} />
              </Grid>
              <Grid item xs={12} lg={8}>
                <SuiBox display="flex" alignItems="center" justifyContent="center">
                  <SuiBox
                    className="aspect-[3/2] w-full"
                    display="flex"
                    alignItems="center"
                    position="center"
                    minHeight="5rem"
                    borderRadius="xl"
                    sx={{
                      backgroundImage: blog.mainimg
                        ? `url(${blog.mainimg})`
                        : ({ functions: { rgba, linearGradient }, palette: { gradients } }) =>
                            `${linearGradient(
                              rgba(gradients.secondary.main, 0.2),
                              rgba(gradients.secondary.state, 0.4)
                            )}`,
                      backgroundSize: "cover",
                      backgroundPosition: "50%",
                      overflow: "hidden",
                    }}
                  />
                </SuiBox>
              </Grid>
            </Grid>
            <Grid container mt={5} spacing={5} direction="row" alignItems="stretch">
              <Grid item xs={12} lg={4}>
                {validTableOfContent && <TableOfContents />}
              </Grid>
              <Grid item xs={12} lg={8}>
                <main style={{ fontSize: 20 }}>{parse(blog.content)}</main>
              </Grid>
            </Grid>
            {blog.status == "published" && (
              <SuiBox mt={5}>
                <Comments subjectid={blog.id} />
              </SuiBox>
            )}
          </Grid>
        </Grid>
      </SuiBox>
    )
  );
};

export default BlogViewer;
